/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { useDebouncedCallback } from 'use-debounce';
import { useEffect, useState } from 'react';
import { Grid, TextField, Typography, Box, Paper } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DescriptionIcon from '@mui/icons-material/Description';
import { NumericFormat } from 'react-number-format';
/**
 * The internal dependencies
 */
import { UpdateChangeDtoStatusEnum } from '@codefluegel/zeta-change-typescript-client';
import { useTranslation } from 'react-i18next';
import { useChangeApiFactory } from '../ApiClient';
import { useChangeStore } from '../store/changeStore';
import { NotificationTypes } from '../types/notification';
import NotificationMessages from '../constants/notification';
import { useStorex } from '../store/UIStore';
import { isChangeManager } from '../utils/permissionHelper';
import ButtonsGroup from '../components/ButtonsGroup/ButtonsGroup';
import ChangeStatusDialogWrapper from '../components/Dialog/ChangeStatusDialogWrapper';
import { DialogType } from '../types/changes';
import LocalizationWrapper from '../components/Date/LocalizationWrapper';
import TodoCard from '../components/TodoCard/TodoCard';
import NumbersPanel from '../components/Panel/NumbersPanel';
import { formatNumber } from '../utils/getTotalTime';

const statusOptions = [{ title: 'Rejected' }, { title: 'Approved' }];

const ProjectExternalPage = observer(() => {
  const { change, setUpdatedChange, workPackages, calculationStore } =
    useChangeStore();
  const { setNotification } = useStorex();
  const changeApi = useChangeApiFactory();

  const { t } = useTranslation();
  const isInternal = change?.preSelectInternalChange;

  const showButtonGroup =
    change?.status === UpdateChangeDtoStatusEnum.CalcComplete ||
    change?.status === UpdateChangeDtoStatusEnum.Check ||
    change?.status ===
      UpdateChangeDtoStatusEnum.ExternalChangeInternallyApproved;

  const showSalesGroup =
    change?.status ===
      UpdateChangeDtoStatusEnum.ExternalChangeInternallyApproved ||
    change?.status === UpdateChangeDtoStatusEnum.InternalApprovalAccepted ||
    change?.status === UpdateChangeDtoStatusEnum.Check ||
    change?.status ===
      UpdateChangeDtoStatusEnum.CrWorkpackagesInInternalProgress ||
    change?.status === UpdateChangeDtoStatusEnum.CrWorkpackagesInProgress ||
    change?.status === UpdateChangeDtoStatusEnum.Rejected ||
    change?.status === UpdateChangeDtoStatusEnum.Closed ||
    change?.status === UpdateChangeDtoStatusEnum.InternalApprovalRejected;

  // Approval Date of Clientm
  const [approvalDatePickerValue, setApprovalDatePickerValue] = useState(
    new Date(change?.approvalDateClient || '') || null,
  );
  const [isApprovalDatePickerOpen, setIsApprovalDatePickerOpen] =
    useState(false);

  // SUBMISSION DATE
  const [submissionCRQuotationToClient, setSubmissionCRQuotationToClient] =
    useState(new Date(change?.submissionCRQuotationToClient || '') || null);
  const [isExternalDatePickerOpen, setIsExternalDatePickerOpen] =
    useState(false);

  // QUOTATION UNTIL  DATE
  const [quoteDatePickerValue, setQuoteDatePickerValue] = useState(
    new Date(change?.quoteValidUntil || '') || null,
  );
  const [isQuoteDatePickerValueOpen, setIsQuoteDatePickerValueOpen] =
    useState(false);

  const editAfterFields =
    change?.status === UpdateChangeDtoStatusEnum.CalcComplete ||
    change?.status ===
      UpdateChangeDtoStatusEnum.CrWorkpackagesInInternalProgress ||
    change?.status ===
      UpdateChangeDtoStatusEnum.ExternalChangeInternallyApproved ||
    change?.status === UpdateChangeDtoStatusEnum.Check;

  const NotificationMessage = NotificationMessages();

  const { releasedMaterialCosts, releasedServiceCosts, releasedHoursCosts } =
    calculationStore;

  const salesMaterialValue = releasedMaterialCosts + releasedServiceCosts;

  const onDesktopDatePickerFocus = (e: any) => {
    setIsApprovalDatePickerOpen(true);
    e.target.blur();
  };

  const onQuotationDatePickerOpen = (e: any) => {
    setIsQuoteDatePickerValueOpen(true);
    e.target.blur();
  };
  const [modalType, setModalType] = useState<DialogType | null>(null);

  const onExternalDesktopDatePickerFocus = (e: any) => {
    setIsExternalDatePickerOpen(true);
    e.target.blur();
  };

  const isManager = isChangeManager(change);

  useEffect(() => {
    if (change) {
      if (change.submissionCRQuotationToClient)
        setSubmissionCRQuotationToClient(
          new Date(change.submissionCRQuotationToClient),
        );
      if (change.approvalDateClient)
        setApprovalDatePickerValue(new Date(change.approvalDateClient));
      if (change.quoteValidUntil)
        setQuoteDatePickerValue(new Date(change.quoteValidUntil));
    }
  }, [change]);

  const debouncedRequest = useDebouncedCallback(
    async (data: { [key: string]: string | number | null }) => {
      try {
        if (change && isManager) {
          const { data: updatedChange } =
            await changeApi.changesControllerUpdate({
              id: change.id,
              updateChangeDto: {
                ...change,
                ...data,
                status: change.status as UpdateChangeDtoStatusEnum,
              },
            });

          if (updatedChange) {
            setUpdatedChange({
              ...change,
              ...data,
              status: change.status as UpdateChangeDtoStatusEnum,
            });

            setNotification({
              type: NotificationTypes.success,
              message: NotificationMessage.chagneUpdate,
            });
          }
        }
      } catch (e: any) {
        setNotification({
          type: NotificationTypes.error,
          message: e.response.data.message || NotificationMessage.error,
        });
        console.error(e);
      }
    },
    300,
  );

  const onChange = (title: string, value: string | number | null) => {
    debouncedRequest({ [title]: value });
  };

  return (
    <Grid
      container
      direction="column"
      css={css`
        padding: 19px 0px 12px 0px;
      `}
    >
      <TodoCard
        text={
          change?.preSelectInternalChange
            ? t('general.infoText.internText')
            : t('general.infoText.externText')
        }
      />
      <Grid
        container
        sx={{
          gap: 1,
          marginTop: 2,
          marginBottom: 3,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Grid size={8}>
          <Grid container spacing={3}>
            {/* Quotation Section */}
            <Grid size={6}>
              <Paper sx={{ p: 2, borderRadius: 2, minHeight: 275 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DescriptionIcon color="primary" sx={{ mr: 1 }} />
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{ fontSize: '1rem' }}
                  >
                    {t('QUOTATION')}
                  </Typography>
                </Box>
                <Box sx={{ p: 1, mb: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {t('costs.quotationValue')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      € {formatNumber(change?.finalQuotationValue ?? 0)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {t('costs.materialValue')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      € {formatNumber(change?.materialValue ?? 0)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {t('costs.discountQuotationValue')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      € {formatNumber(change?.projectDiscount ?? 0)}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 1,
                    borderTop: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {t('costs.totalQuotationValue')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    €{' '}
                    {formatNumber(
                      Number(change?.finalQuotationValue || 0) +
                        Number(change?.materialValue || 0) -
                        Number(change?.projectDiscount || 0),
                    )}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Sales Section */}
            {showSalesGroup && (
              <Grid size={6}>
                <Paper sx={{ p: 2, borderRadius: 2, minHeight: 275 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ShowChartIcon color="success" sx={{ mr: 1 }} />
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color="success.main"
                      sx={{ fontSize: '1rem' }}
                    >
                      {t('SALES')}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {t('costs.sellValue')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        € {formatNumber(releasedHoursCosts ?? 0)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {t('costs.salesMaterialValue')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        € {formatNumber(salesMaterialValue ?? 0)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {t('costs.discountSalesValue')}
                      </Typography>
                      <NumericFormat
                        value={change.salesValueDiscount ?? 0}
                        onValueChange={values => {
                          onChange(
                            'salesValueDiscount',
                            Number(values.floatValue),
                          );
                        }}
                        thousandSeparator
                        decimalScale={2}
                        locale={
                          localStorage.getItem('i18nextLng') === 'de'
                            ? 'de_DE'
                            : 'en_US'
                        }
                        fixedDecimalScale
                        prefix="€ "
                        customInput={({
                          onChange: numericFormatOnChange,
                          onBlur,
                          onFocus,
                          ...props
                        }) => (
                          <TextField
                            {...props}
                            onChange={numericFormatOnChange}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            name="salesValueDiscount"
                            css={css`
                              .MuiInput-underline:before {
                                border-bottom: 1px solid #ececec;
                              }
                              .MuiInput-input {
                                padding: 4px 0 4px;
                                text-align: right; /* Right align the text */
                                font-size: 14px;
                                font-weight: 300;
                              }
                              .MuiInputLabel-asterisk {
                                color: #ff0000;
                              }
                            `}
                            variant="standard"
                            disabled={!isManager || !editAfterFields}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      p: 1,
                      borderTop: '1px solid #ccc',
                    }}
                  >
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color="success.main"
                    >
                      {t('costs.totalSalesValue')}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color="success.main"
                    >
                      €{' '}
                      {formatNumber(
                        Number(releasedHoursCosts || 0) +
                          Number(salesMaterialValue || 0) -
                          Number(change?.salesValueDiscount || 0),
                      )}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
          {showSalesGroup && (
            <Grid container spacing={3} sx={{ marginTop: 2 }}>
              <Grid size={6}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    minHeight: 275,
                    flexDirection: 'column',
                    display: 'flex',
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{ mb: 2, fontSize: '1rem' }}
                  >
                    {isInternal
                      ? t('general.infoText.internalApprovalText')
                      : t('general.infoText.externalApprovalText')}
                  </Typography>
                  <LocalizationWrapper>
                    <DesktopDatePicker
                      open={isExternalDatePickerOpen}
                      onOpen={() => setIsExternalDatePickerOpen(true)}
                      onClose={() => setIsExternalDatePickerOpen(false)}
                      label={t('dates.externalApprovalCustomerDate')}
                      format="dd.MM.yyyy"
                      disabled={!isManager || !editAfterFields}
                      value={submissionCRQuotationToClient}
                      onChange={newDate => {
                        if (
                          change &&
                          !change.submissionCRQuotationToClient &&
                          !newDate
                        ) {
                          return;
                        }
                        if (newDate) {
                          setSubmissionCRQuotationToClient(newDate);
                          onChange(
                            'submissionCRQuotationToClient',
                            newDate.toDateString() || null,
                          );
                        }
                      }}
                      slotProps={{
                        textField: {
                          style: {
                            width: '50%',
                            marginTop: 16,
                            backgroundColor: '#fff',
                            borderRadius: 8,
                            paddingLeft: 8,
                          },
                          variant: 'outlined', // change from "standard" -> "outlined"
                          InputProps: {
                            onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                              onExternalDesktopDatePickerFocus(e),
                            sx: {
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#ddd',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#80acd4',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                {
                                  borderColor: '#80acd4',
                                  boxShadow: '0 0 4px rgba(128,172,212,0.4)',
                                },
                            },
                          },
                          InputLabelProps: {
                            sx: {
                              paddingLeft: 1,
                              fontWeight: 600,
                              color: '#333',
                            },
                          },
                        },
                        inputAdornment: { sx: { paddingRight: 1 } },
                      }}
                    />
                  </LocalizationWrapper>
                  <LocalizationWrapper>
                    <DesktopDatePicker
                      open={isApprovalDatePickerOpen}
                      onOpen={() => setIsApprovalDatePickerOpen(true)}
                      onClose={() => setIsApprovalDatePickerOpen(false)}
                      label={t('dates.approvalDatecustomer')}
                      format="dd.MM.yyyy"
                      disabled={!isManager || !editAfterFields}
                      value={approvalDatePickerValue}
                      onChange={newDate => {
                        if (change && !change.approvalDateClient && !newDate) {
                          return;
                        }
                        if (newDate) {
                          setApprovalDatePickerValue(newDate);
                          onChange(
                            'approvalDateClient',
                            newDate.toDateString() || null,
                          );
                        }
                      }}
                      slotProps={{
                        textField: {
                          style: {
                            width: '50%',
                            marginTop: 16,
                            backgroundColor: '#fff',
                            borderRadius: 8,
                            paddingLeft: 8,
                          },
                          variant: 'outlined', // change from "standard" -> "outlined"
                          InputProps: {
                            onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                              onDesktopDatePickerFocus(e),
                            sx: {
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#ddd',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#80acd4',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                {
                                  borderColor: '#80acd4',
                                  boxShadow: '0 0 4px rgba(128,172,212,0.4)',
                                },
                            },
                          },
                          InputLabelProps: {
                            sx: {
                              paddingLeft: 1,
                              fontWeight: 600,
                              color: '#333',
                            },
                          },
                        },
                        inputAdornment: { sx: { paddingRight: 1 } },
                      }}
                    />
                  </LocalizationWrapper>
                  <LocalizationWrapper>
                    <DesktopDatePicker
                      open={isQuoteDatePickerValueOpen}
                      onOpen={() => setIsQuoteDatePickerValueOpen(true)}
                      onClose={() => setIsQuoteDatePickerValueOpen(false)}
                      label={t('dates.quoteValidUntilDate')}
                      format="dd.MM.yyyy"
                      disabled={!isManager || !editAfterFields}
                      value={quoteDatePickerValue}
                      onChange={newDate => {
                        if (change && !change.quoteValidUntil && !newDate) {
                          return;
                        }
                        if (newDate) {
                          setQuoteDatePickerValue(newDate);
                          onChange(
                            'quoteValidUntil',
                            newDate.toDateString() || null,
                          );
                        }
                      }}
                      slotProps={{
                        textField: {
                          style: {
                            width: '50%',
                            marginTop: 16,
                            backgroundColor: '#fff',
                            borderRadius: 8,
                            paddingLeft: 8,
                          },
                          variant: 'outlined', // change from "standard" -> "outlined"
                          InputProps: {
                            onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                              onQuotationDatePickerOpen(e),
                            sx: {
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#ddd',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#80acd4',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                {
                                  borderColor: '#80acd4',
                                  boxShadow: '0 0 4px rgba(128,172,212,0.4)',
                                },
                            },
                          },
                          InputLabelProps: {
                            sx: {
                              paddingLeft: 1,
                              fontWeight: 600,
                              color: '#333',
                            },
                          },
                        },
                        inputAdornment: { sx: { paddingRight: 1 } },
                      }}
                    />
                  </LocalizationWrapper>
                </Paper>
              </Grid>
              <Grid size={6}>
                <Paper sx={{ p: 2, borderRadius: 2, minHeight: 275 }}>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{ mb: 2, fontSize: '1rem' }}
                  >
                    {t('costs.statusInformation')}
                  </Typography>{' '}
                  <Grid container flexDirection="row">
                    <Grid size={6}>
                      <Box
                        css={css`
                          padding: 15px;
                          display: flex;
                          flex-direction: column;
                          gap: 16px;
                        `}
                      >
                        <Autocomplete
                          popupIcon={
                            <ExpandMoreIcon
                              color="primary"
                              css={css`
                                color: #80acd4;
                              `}
                            />
                          }
                          css={css`
                            icon: {
                              fill: 'red';
                            }
                            .MuiInput-underline:before {
                              border-bottom: 1px solid #ececec;
                            }
                            .MuiInputLabel-asterisk {
                              color: #ff0000;
                            }
                          `}
                          disabled={!isManager || !editAfterFields}
                          value={change?.statusClient || null}
                          defaultValue={change?.statusClient || null}
                          options={statusOptions.map(option => option.title)}
                          onChange={(e, value) =>
                            onChange('statusClient', value || '')
                          }
                          renderInput={params => (
                            <TextField
                              {...params}
                              label={t('customer.customerStatus')}
                              variant="standard"
                              InputProps={{
                                ...params.InputProps,
                                style: {
                                  paddingLeft: '16px',
                                  paddingBottom: '6px',
                                  marginBottom: '16px',
                                  fontSize: '14px',
                                  fontWeight: '300',
                                },
                              }}
                              InputLabelProps={{
                                style: {
                                  fontWeight: 600,
                                  color: '#333333',
                                },
                              }}
                            />
                          )}
                        />

                        <TextField
                          name="rejectedStatusExplanation"
                          css={css`
                            .MuiInput-underline:before {
                              border-bottom: 1px solid #ececec;
                            }
                          `}
                          defaultValue={change?.rejectedStatusExplanation || ''}
                          onBlur={e => onChange(e.target.name, e.target.value)}
                          label={
                            change?.statusClient === 'Approved'
                              ? t('general.reason')
                              : t('general.RejectionReason')
                          }
                          variant="standard"
                          disabled={!isManager || !editAfterFields}
                          InputProps={{
                            style: {
                              paddingRight: '9px',
                              paddingBottom: '10px',
                              paddingLeft: '16px',
                              marginBottom: '16px',
                              fontSize: '14px',
                              fontWeight: '300',
                            },
                          }}
                          InputLabelProps={{
                            style: {
                              fontWeight: 600,
                              color: '#333333',
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      {change?.statusClient === 'Approved' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <TextField
                            css={css`
                              .MuiInput-underline:before {
                                border-bottom: 1px solid #ececec;
                              }
                              .MuiInputLabel-asterisk {
                                color: #ff0000;
                              }
                              width: 200px;
                            `}
                            required
                            id="changeNoWebTool"
                            label={t('change.changeNumberWebtool')}
                            defaultValue={change?.changeNoWebTool || ''}
                            onChange={e =>
                              onChange(e.target.name, e.target.value || null)
                            }
                            variant="standard"
                            name="changeNoWebTool"
                            InputProps={{
                              style: {
                                paddingLeft: '16px',
                                paddingBottom: '6px',
                                marginBottom: '16px',
                                fontSize: '14px',
                              },
                            }}
                            InputLabelProps={{
                              style: {
                                paddingLeft: '20px',
                                fontWeight: 600,
                                color: '#333333',
                              },
                            }}
                          />

                          <TextField
                            onChange={e =>
                              onChange(e.target.name, e.target.value || null)
                            }
                            css={css`
                              .MuiInput-underline:before {
                                border-bottom: 1px solid #ececec;
                              }
                              .MuiInputLabel-asterisk {
                                color: #ff0000;
                              }
                            `}
                            required
                            id="webToolVersion"
                            name="webToolVersion"
                            label={t('general.webtoolVersion')}
                            defaultValue={change?.webToolVersion || ''}
                            variant="standard"
                            InputProps={{
                              style: {
                                paddingLeft: '16px',
                                paddingBottom: '6px',
                                marginBottom: '16px',
                                fontSize: '14px',
                              },
                            }}
                            InputLabelProps={{
                              style: {
                                paddingLeft: '20px',
                                fontWeight: 600,
                                color: '#333333',
                              },
                            }}
                          />
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid size={3}>
          {change && <NumbersPanel change={change} />}

          {change && showButtonGroup && (
            <ButtonsGroup
              isChangeExist={!!change}
              changeStatus={change?.status || ''}
              workPackages={workPackages}
              change={change}
              onClickBtn={e =>
                setModalType(e.currentTarget.dataset.type as DialogType)
              }
            />
          )}
        </Grid>
      </Grid>

      <ChangeStatusDialogWrapper
        modalType={modalType}
        setModalType={m => setModalType(m)}
      />
    </Grid>
  );
});

export default ProjectExternalPage;
